import { arraysEqualIgnoreOrder } from '@/utilities/arrayUtils';
import type { EntityState } from '../generated';
import type { BaseObj } from '../BaseObj';
import { TimeSpan } from '../TimeSpan';

type EntityStateSetter = (newState: { EntityStateWithChildrenConsidered: EntityState; SelfEntityState: EntityState }) => void;

export const useBaseObjEntityStateHandler = <TBaseObj extends BaseObj>(
    initalEntityState: EntityState,
    entityStateSetter: EntityStateSetter,
) => {
    /**
     * Set of the properties that have changed on child object, or within arrays of child objects
     */
    const changedChildProperties: Set<keyof TBaseObj> = new Set<keyof TBaseObj>();

    /**
     * Holds original values for properties that are primitives (and dates)
     *
     * Complex objects (BaseObj) handle their own original values
     */
    const originalValues = new Map<keyof TBaseObj, any>();

    /**
     * Holds original values for properties in child objects that are not BaseObj
     *
     * Here things starts to fall apart a little bit.
     *
     * If the value is itself an object we will do comparisons by object reference.
     */
    const originalValuesInChildrenThatAreNotBaseObj = new Map<string, any>();

    /**
     * Callback to notify parent that something has changed in this object
     *
     * Only applicable if the the object has a parent
     */
    let notifyParentOfModificationCallback: (() => void) | undefined = undefined;

    /**
     * Creates a proxy wrapped array with a callback so we can catch stuff like .push on the array
     */
    const createArrayWithMutationCallback = <T>(args: { initalValue: T[]; callback: (oldVal: T[], newVal: T[]) => void }): T[] => {
        const { initalValue, callback } = args;
        const arr = [...initalValue];
        return new Proxy(arr, {
            get(target, prop, receiver) {
                // Intercept mutating methods
                if (
                    typeof prop === 'string' &&
                    ['push', 'pop', 'shift', 'unshift', 'splice', 'sort', 'reverse', 'copyWithin', 'fill'].includes(prop)
                ) {
                    return (...innerArgs: T[]) => {
                        const original = [...target];
                        // Apply the mutation
                        // @ts-ignore
                        const result = Array.prototype[prop].apply(target, innerArgs);

                        callback(original, target);

                        // Return the result of the mutation
                        return result;
                    };
                }
                // Default behavior for all other properties/methods
                return Reflect.get(target, prop, receiver);
            },
            // This handles direct index assignments like arr[0] = value;
            // The actual setting of the entire field is handled in the fields setter in the TBaseObj itself
            set(target, prop, value, receiver) {
                const original = [...target];

                const result = Reflect.set(target, prop, value, receiver);

                callback(original, target);

                // return the result
                return result;
            },
        });
    };

    const createProxyWrapperWithMutationTracking = <T extends object>(args: {
        childObject: T | null;
        childField: keyof TBaseObj;
    }): T | null => {
        const { childObject, childField } = args;
        if (typeof childField !== 'string') {
            throw new Error('createProxyWrapperWithMutationTracking does not support non-string child fields');
        }

        // If we dont have an object, no reason to proxy it
        if (childObject === null) {
            return null;
        }

        return new Proxy(childObject, {
            set(target, prop, value, receiver) {
                if (typeof prop !== 'string') {
                    return Reflect.set(target, prop, value, receiver); // If we overwrite a function I suppose. We should really never get here
                }

                // Notify of property change
                propertyInChildObjectThatIsNotBaseObjChanged(childField + '.' + prop, target[prop as keyof T], value);

                const result = Reflect.set(target, prop, value, receiver);

                // return the result
                return result;
            },
        });
    };

    /**
     * Keep track of the current self state
     */
    let previouslyAppliedSelfState: EntityState = initalEntityState;

    /**
     * Notify the handler if the object changes its own state.
     *
     * This is basically to support setting the state to deleted
     * @param newState The new self state
     */
    const entityStateChanged = (newState: 'Unchanged' | 'Added' | 'Deleted') => {
        previouslyAppliedSelfState = newState;
        updateEntityStateOnObject();
    };

    /**
     * Finds out what the correct EntityStates are based on the original states and what has changed
     */
    const updateEntityStateOnObject = () => {
        let selfState = previouslyAppliedSelfState;
        let visibleState: EntityState;

        // If we are deleted or added, we dont care if anything else has changed
        if (['Deleted', 'Added'].includes(previouslyAppliedSelfState)) {
            // Make sure the visible state is the same as the self state
            visibleState = previouslyAppliedSelfState;
        } else {
            // If we have self changes then we are modified both visibly and selfly
            if (originalValues.size > 0) {
                selfState = 'Modified';
                visibleState = selfState;
            } else {
                selfState = 'Unchanged';
                visibleState = selfState;
            }

            // If we have child changes then we are modified visibly
            if (changedChildProperties.size > 0) {
                visibleState = 'Modified';
            }
        }

        previouslyAppliedSelfState = selfState;

        /**
         * Apply state to object
         */
        entityStateSetter({ EntityStateWithChildrenConsidered: visibleState, SelfEntityState: selfState });

        /**
         * Notify any parent that something happened
         */
        notifyParentOfModificationCallback?.();
    };

    /**
     * Handles changes of array of base objects
     *
     * This will basically just check if any of the objects report that they are changed, and if that is the case then the field is changed
     */
    const childArrayOfBaseObjsChanged = (childField: keyof TBaseObj, values: BaseObj[]) => {
        if (values?.some((b) => b.EntityState !== 'Unchanged')) {
            changedChildProperties.add(childField);
        } else {
            changedChildProperties.delete(childField);
        }

        updateEntityStateOnObject();
    };

    /**
     * Checks if a child entity has changed
     *
     * Essentially this checks if the child has EntityState different from Unchanged
     *
     * We also try to check if the object has been replaced by checking its ID against the original value
     * @param childField The name of field in this class that changed
     */
    const childBaseObjChanged = <TChild extends BaseObj>(childField: keyof TBaseObj, value: TChild | null) => {
        if (value?.EntityState ?? 'Unchanged' !== 'Unchanged') {
            changedChildProperties.add(childField);
            updateEntityStateOnObject();
        } else {
            changedChildProperties.delete(childField);
        }
    };

    /**
     * Checks if two values are the same
     *
     * Triple equals wont do it if we are dealing with dates or timespans, so we use this utility function
     * @param a First value to compare
     * @param b Second value to compare
     * @returns True if equal, false otherwise
     */
    const isValuesEqual = (a: any, b: any): boolean => {
        // Date prop
        if (a instanceof Date || b instanceof Date) {
            if (a instanceof Date && b instanceof Date) {
                return a.getTime() === b.getTime();
            } else {
                // One is date, the other isnt
                return false;
            }
        }

        // Timespan prop
        if (a instanceof TimeSpan || b instanceof TimeSpan) {
            if (a instanceof TimeSpan && b instanceof TimeSpan) {
                return a.TotalMilliseconds === b.TotalMilliseconds;
            } else {
                // One is TimeSpan, the other isnt (the other one is null or undefined)
                return false;
            }
        }

        // Arrays
        if (Array.isArray(a) || Array.isArray(b)) {
            if (Array.isArray(a) && Array.isArray(b)) {
                const firstVal = a.at(0) || b.at(0);
                // Array of dates
                if (firstVal instanceof Date) {
                    return arraysEqualIgnoreOrder(
                        ((a ?? []) as Date[]).map((d) => d.getTime()),
                        ((b ?? []) as Date[]).map((d) => d.getTime()),
                    );
                }
                // Array of Timespans
                else if (firstVal instanceof TimeSpan) {
                    return arraysEqualIgnoreOrder(
                        ((a ?? []) as TimeSpan[]).map((ts) => ts.TotalMilliseconds),
                        ((b ?? []) as TimeSpan[]).map((ts) => ts.TotalMilliseconds),
                    );
                }
                // Array of primitives
                else {
                    return arraysEqualIgnoreOrder(a, b);
                }
            } else {
                // One is array, the other isnt
                return false;
            }
        }

        return a === b;
    };

    /**
     * This is essentially the same as {@link propertyChanged}, but we have less strict typing
     */
    const propertyInChildObjectThatIsNotBaseObjChanged = (propChain: string, oldVal: any, newVal: any) => {
        if (isValuesEqual(oldVal, newVal)) {
            return;
        }

        if (originalValuesInChildrenThatAreNotBaseObj.has(propChain)) {
            const originalVal = originalValuesInChildrenThatAreNotBaseObj.get(propChain);
            if (isValuesEqual(originalVal, newVal)) {
                originalValuesInChildrenThatAreNotBaseObj.delete(propChain);
                updateEntityStateOnObject();
                return;
            }
            // We can return here since we are already modified. We are still modified, just with a different value
            return;
        }

        originalValuesInChildrenThatAreNotBaseObj.set(propChain, oldVal);
        updateEntityStateOnObject();
    };

    const propertyChanged = <TProp extends keyof TBaseObj>(property: TProp, oldVal: TBaseObj[TProp], newVal: TBaseObj[TProp]) => {
        // If the value hasent actually changed we dont need to do anything
        if (isValuesEqual(oldVal, newVal)) {
            return;
        }

        // If we have an original value, we can compare against that
        if (originalValues.has(property)) {
            const originalVal = originalValues.get(property);
            if (isValuesEqual(originalVal, newVal)) {
                originalValues.delete(property);
                updateEntityStateOnObject();
                return;
            }
            // We can return here since we are already modified. We are still modified, just with a different value
            return;
        }

        // We didnt have an original value, so we save the value as the original and set modified
        originalValues.set(property, oldVal);
        updateEntityStateOnObject();
    };

    /**
     * Checks if any of the given properties are changed
     * @param properties The properties to check
     * @returns True if changed, false otherwise
     */
    const isPropertyChanged = <TProp extends keyof TBaseObj>(...properties: TProp[]): boolean => {
        return properties.some((property) => originalValues.has(property));
    };

    /**
     * Get all the changed properties as an array
     *
     * This is used by the serialization so we can send the list of changed properties to the backend
     * @returns Array of changed properties
     */
    const getChangedPropertiesArray = (): (string | number | symbol)[] => Array.from(originalValues.keys());

    const setNotifyParentOfModificationCallback = (callback: () => void) => {
        // We replace the objects with the same objects so this check really isnt good enough.
        // We could send the current parent and store it and check if we are changing parents, but for now this is just disabled
        // if (notifyParentOfModificationCallback !== undefined) {
        //     throw new Error(
        //         'Attempted to set a notifyParentOfModificationCallback when one was already set. This amounts to changing the parent of an object. That is not allowed!',
        //     );
        // }
        notifyParentOfModificationCallback = callback;
    };

    return {
        createArrayWithMutationCallback,
        setNotifyParentOfModificationCallback,
        propertyChanged,
        childBaseObjChanged,
        childArrayOfBaseObjsChanged,
        entityStateChanged,
        createProxyWrapperWithMutationTracking,
        isPropertyChanged,
        getChangedPropertiesArray,
    };
};
